import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Heart, ShieldAlert, Sparkles, Lock, ArrowRight, ArrowLeft, LogOut } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// IMPORTANT: must mirror CURRENT_DISCLAIMER_VERSIONS in legal_disclaimers.py
const STEPS = [
  {
    key: 'medical',
    version: '1.0',
    icon: ShieldAlert,
    accent: 'bg-rose-100 text-rose-700',
    title: 'Important Information About Calm Pathways',
    checkboxLabel:
      'I understand that Calm Pathways is not a substitute for professional medical care or emergency services.',
    primaryLabel: 'Continue',
    secondaryLabel: 'Exit App',
    body: (
      <div className="space-y-3 text-[#57534E] leading-relaxed text-[15px]">
        <p>
          Calm Pathways is an educational and organizational wellness tool designed for parents and caregivers.
        </p>
        <p>The app does not provide:</p>
        <ul className="list-disc list-outside pl-5 space-y-1 marker:text-stone-400">
          <li>medical advice,</li>
          <li>diagnosis,</li>
          <li>treatment,</li>
          <li>therapy,</li>
          <li>emergency services,</li>
          <li>or clinical supervision.</li>
        </ul>
        <p>
          The app is not monitored in real time and should never be used during medical or psychiatric emergencies.
        </p>
        <p>
          Always consult qualified healthcare professionals regarding medical concerns, medications, supplements,
          or treatment decisions related to your child.
        </p>
      </div>
    ),
  },
  {
    key: 'ai_wellness',
    version: '1.0',
    icon: Sparkles,
    accent: 'bg-violet-100 text-violet-700',
    title: 'AI & Wellness Tracking Acknowledgement',
    checkboxLabel:
      'I understand that AI-generated content, trends, correlations, and wellness insights are informational only and not medical advice.',
    primaryLabel: 'Continue',
    secondaryLabel: 'Back',
    body: (
      <div className="space-y-3 text-[#57534E] leading-relaxed text-[15px]">
        <p>
          Certain features within Calm Pathways may use artificial intelligence (&ldquo;AI&rdquo;) to generate
          educational summaries, trends, correlations, organizational insights, or informational responses.
        </p>
        <p>AI-generated content:</p>
        <ul className="list-disc list-outside pl-5 space-y-1 marker:text-stone-400">
          <li>may be incomplete, inaccurate, outdated, or inappropriate,</li>
          <li>is not reviewed by healthcare professionals,</li>
          <li>
            and should not be relied upon for diagnosis, treatment decisions, medication changes, supplement
            decisions, or emergencies.
          </li>
        </ul>
        <p>
          The app may also display trends, correlations, or wellness insights based on information entered into
          the app. These insights are informational only and do not establish medical causation, diagnosis, or
          clinical conclusions.
        </p>
      </div>
    ),
  },
  {
    key: 'privacy_responsibility',
    version: '1.0',
    icon: Lock,
    accent: 'bg-sky-100 text-sky-700',
    title: 'Privacy & Parent Responsibility',
    checkboxLabel:
      'I understand and consent to the storage and processing of information as described in the Privacy Policy.',
    primaryLabel: 'Agree & Continue',
    secondaryLabel: 'Back',
    body: (
      <div className="space-y-3 text-[#57534E] leading-relaxed text-[15px]">
        <p>Calm Pathways is intended for use by parents, guardians, and caregivers only.</p>
        <p>By using the app, you acknowledge that:</p>
        <ul className="list-disc list-outside pl-5 space-y-1 marker:text-stone-400">
          <li>you are responsible for information entered into the app,</li>
          <li>you have authority to provide information regarding your child,</li>
          <li>
            and information entered into the app may be electronically stored and processed to provide app functionality.
          </li>
        </ul>
        <p>
          While we use commercially reasonable safeguards designed to help protect information, no electronic
          system can guarantee absolute security.
        </p>
      </div>
    ),
  },
];

const detectPlatform = () => {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (/iphone|ipad|ios/i.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'web';
};

const FirstSignInDisclaimerFlow = () => {
  const { user, logout } = useAuth();

  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [accepted, setAccepted] = useState({}); // { medical: bool, ai_wellness: bool, privacy_responsibility: bool }
  const [pendingTypes, setPendingTypes] = useState([]); // types the server still needs
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Fetch the user's acceptance status whenever the user changes
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!user) {
        setOpen(false);
        return;
      }
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/legal-disclaimers/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const data = res.data || {};
        const pending = data.pending || [];
        setPendingTypes(pending);
        if (!data.all_accepted) {
          // Start at the first pending step in the canonical order
          const firstIdx = STEPS.findIndex((s) => pending.includes(s.key));
          setStepIndex(firstIdx >= 0 ? firstIdx : 0);
          setOpen(true);
        } else {
          // Make sure the completion flag is set so WelcomePopup can show.
          try {
            localStorage.setItem(`cp_disclaimers_complete_${user.id}`, '1');
          } catch (_) { /* storage may be unavailable */ }
          setOpen(false);
        }
      } catch (err) {
        // If the status check fails we don't block the app, but log it.
        // The user can re-trigger by signing out and back in.
        // eslint-disable-next-line no-console
        console.warn('Disclaimer status fetch failed:', err?.response?.data || err.message);
        if (!cancelled) setOpen(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const currentStep = STEPS[stepIndex];

  const recordAcceptance = useCallback(async (stepKey, version) => {
    const token = localStorage.getItem('token');
    await axios.post(
      `${API}/legal-disclaimers/accept`,
      {
        disclaimer_type: stepKey,
        version,
        platform: detectPlatform(),
      },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  }, []);

  const handlePrimary = async () => {
    if (!currentStep) return;
    if (!accepted[currentStep.key]) return;

    try {
      setSubmitting(true);
      setError('');
      // Only call the API if the server doesn't already have this acceptance
      if (pendingTypes.includes(currentStep.key)) {
        await recordAcceptance(currentStep.key, currentStep.version);
      }

      const nextIdx = stepIndex + 1;
      if (nextIdx >= STEPS.length) {
        // Signal to other onboarding components (WelcomePopup) that
        // the user has finished the legally-required acknowledgements.
        try {
          localStorage.setItem(`cp_disclaimers_complete_${user.id}`, '1');
        } catch (_) { /* storage may be unavailable */ }
        setOpen(false);
      } else {
        setStepIndex(nextIdx);
      }
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          'Unable to record your acknowledgement right now. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSecondary = () => {
    if (!currentStep) return;
    if (stepIndex === 0) {
      // "Exit App" — log them out
      logout?.();
    } else {
      setStepIndex(Math.max(0, stepIndex - 1));
    }
  };

  if (!user || loading || !open || !currentStep) return null;

  const Icon = currentStep.icon;
  const isChecked = !!accepted[currentStep.key];

  return (
    <Dialog open={open} onOpenChange={() => { /* not user-dismissible */ }}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden z-[120]"
        data-testid="first-signin-disclaimer-dialog"
        // Disable outside-click + ESC close so the user MUST act on the buttons
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-primary/10 to-secondary/10 px-6 pt-6 pb-5 border-b border-stone-200">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${currentStep.accent}`}>
              <Icon className="w-6 h-6" strokeWidth={1.6} />
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium text-stone-500 tracking-wider uppercase mb-1">
                Step {stepIndex + 1} of {STEPS.length}
              </p>
              <DialogHeader className="space-y-1 text-left">
                <DialogTitle className="text-xl md:text-2xl font-nunito font-bold text-[#2D2D2D]">
                  {currentStep.title}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Required acknowledgement before using Calm Pathways.
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>

          {/* Progress dots */}
          <div className="flex items-center gap-2 mt-4 ml-16">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i < stepIndex
                    ? 'w-6 bg-primary'
                    : i === stepIndex
                    ? 'w-8 bg-primary'
                    : 'w-6 bg-stone-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 max-h-[55vh] overflow-y-auto">
          {currentStep.body}
        </div>

        {/* Required checkbox */}
        <div className="px-6 pb-4">
          <label
            className="flex items-start gap-3 p-4 rounded-lg bg-stone-50 border border-stone-200 cursor-pointer hover:bg-stone-100 transition-colors"
            data-testid={`disclaimer-checkbox-row-${currentStep.key}`}
          >
            <Checkbox
              checked={isChecked}
              onCheckedChange={(v) =>
                setAccepted((prev) => ({ ...prev, [currentStep.key]: !!v }))
              }
              className="mt-0.5"
              data-testid={`disclaimer-checkbox-${currentStep.key}`}
            />
            <span className="text-sm text-[#2D2D2D] leading-relaxed">
              {currentStep.checkboxLabel}
            </span>
          </label>
        </div>

        {error && (
          <div className="px-6 pb-2">
            <p className="text-sm text-red-600" role="alert">{error}</p>
          </div>
        )}

        {/* Buttons */}
        <div className="px-6 pb-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-stone-100 pt-4">
          <Button
            variant="outline"
            onClick={handleSecondary}
            className="sm:w-auto w-full"
            data-testid={`disclaimer-secondary-${currentStep.key}`}
          >
            {stepIndex === 0 ? (
              <><LogOut className="w-4 h-4 mr-2" />{currentStep.secondaryLabel}</>
            ) : (
              <><ArrowLeft className="w-4 h-4 mr-2" />{currentStep.secondaryLabel}</>
            )}
          </Button>

          <Button
            onClick={handlePrimary}
            disabled={!isChecked || submitting}
            className="bg-primary text-white hover:bg-primary/90 sm:w-auto w-full disabled:opacity-50"
            data-testid={`disclaimer-primary-${currentStep.key}`}
          >
            {submitting ? (
              <><Heart className="w-4 h-4 mr-2 animate-pulse" />Saving…</>
            ) : (
              <>{currentStep.primaryLabel}<ArrowRight className="w-4 h-4 ml-2" /></>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FirstSignInDisclaimerFlow;
